import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  FileText, Printer, RotateCcw, Save, Building2, ClipboardCheck,
  Upload, AlertTriangle, Hash, Loader2, ListOrdered, History, Search,
  Download, FileSpreadsheet,
} from "lucide-react";

const DRAFT_KEY = "edf_form_draft_v1";

const emptyForm = {
  customs_security_no: "",
  form_no: "",              // filled from pool on print (do NOT let user edit; peek shows next)
  nature_of_cargo: "Non-Government",
  mode_of_transport: "Post/Couriers",
  shipping_bill_no: "",
  shipping_bill_date: "",
  category_of_exporter: "100% EOU",
  rbi_approval: "",
  iec: "",
  ad_code: "",
  exporter_name_address: "",
  ad_name_address: "",
  consignee_name_address: "AS PER PACKING SHEET",
  port_of_loading: "MUMBAI",
  third_party_name_address: "",
  country_of_destination: "AS PER PACKING SHEET",
  port_of_discharge: "MUMBAI",
  commodity_description: "ADVANCE PAYMENT",
  payment_acu: "No",
  leo_date: "",
  state_of_origin: "MAHARASHTRA",
  fob_words: "",
  custom_assessable_value: "",
  invoice_no: "",
  invoice_date: "",
  invoice_currency: "$",
  invoice_amount: "",
  nature_of_contract: "FOB",
  fob_fc: "",
  fob_rate: "",
  fob_inr: "",
  freight_fc: "",
  freight_rate: "",
  freight_inr: "",
  insurance_fc: "",
  insurance_rate: "",
  insurance_inr: "",
  commission_fc: "",
  discount_fc: "",
  other_deduction_fc: "0.00",
  packing_fc: "",
  packing_rate: "",
  packing_inr: "0.00",
  net_realisable_value: "",
  post_office_name: "",
  parcel_receipts: "",
  declaration_date: "",
};

const buildAddress = (co) => {
  if (!co) return "";
  return [co.name, co.address_line_1, co.address_line_2, co.address_line_3, co.pincode]
    .filter(Boolean).join(", ");
};

const fmtInvDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}-${mm}-${d.getFullYear()}`;
  } catch { return ""; }
};

// Underline-style form input defined at module scope so React does NOT
// remount it on every keystroke (which would drop focus).
const EdfField = ({ value, onChange, placeholder, className = "", multiline, readOnly, ...rest }) => (
  multiline ? (
    <textarea
      value={value || ""}
      readOnly={readOnly}
      onChange={(e) => onChange && onChange(e.target.value)}
      placeholder={placeholder}
      rows={2}
      className={`w-full text-xs border-0 border-b border-slate-300 focus:outline-none focus:border-indigo-500 bg-transparent px-1 py-0.5 resize-none ${className}`}
      {...rest}
    />
  ) : (
    <input
      type="text"
      value={value || ""}
      readOnly={readOnly}
      onChange={(e) => onChange && onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full text-xs border-0 border-b border-slate-300 focus:outline-none focus:border-indigo-500 bg-transparent px-1 py-0.5 ${className}`}
      {...rest}
    />
  )
);

const EdfFormPanel = ({ user }) => {
  const [companies, setCompanies] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedShipmentId, setSelectedShipmentId] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [poolStatus, setPoolStatus] = useState({ total: 0, consumed: 0, available: 0, low_stock: true, low_stock_threshold: 10 });
  const [nextFormNo, setNextFormNo] = useState("");
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const printRef = useRef(null);

  const refreshPool = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/edf/next-form-no`, { headers: getAuthHeader() });
      setNextFormNo(r.data.next_form_no || "");
      setPoolStatus(r.data.pool_status);
    } catch (e) { /* ignore */ }
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [coRes, shRes] = await Promise.all([
        axios.get(`${API}/companies`, { headers: getAuthHeader() }),
        axios.get(`${API}/shipments?scope=all`, { headers: getAuthHeader() })
             .catch(() => ({ data: { shipments: [] } })),
      ]);
      const coList = Array.isArray(coRes.data) ? coRes.data : (coRes.data.companies || []);
      setCompanies(coList);
      setShipments(shRes.data.shipments || []);
      await refreshPool();
    } catch (e) {
      toast.error("Failed to load reference data");
    }
  }, [refreshPool]);

  useEffect(() => {
    loadData();
    // First priority: honor a reprint request from another panel (e.g., ShipmentDetail).
    // If `edf_reprint_pending` is set, load that snapshot in instead of any local draft.
    let reprintApplied = false;
    try {
      const rawR = localStorage.getItem("edf_reprint_pending");
      if (rawR) {
        const p = JSON.parse(rawR);
        if (p && p.form_no) {
          setForm(prev => ({ ...prev, ...(p.snapshot || {}), form_no: p.form_no }));
          if (p.snapshot?.company_id) setSelectedCompanyId(p.snapshot.company_id);
          reprintApplied = true;
          toast.success(`Loaded ${p.form_no}${p.from_shipment_no ? ` from ${p.from_shipment_no}` : ""} — press Ctrl+P to reprint`);
          localStorage.removeItem("edf_reprint_pending");
        }
      }
    } catch { /* ignore */ }
    if (reprintApplied) return;
    // Otherwise restore any saved draft
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          setForm(prev => ({ ...prev, ...parsed.form }));
          if (parsed.selectedCompanyId) setSelectedCompanyId(parsed.selectedCompanyId);
          if (parsed.selectedShipmentId) setSelectedShipmentId(parsed.selectedShipmentId);
        }
      }
    } catch { /* ignore */ }
  }, [loadData]);

  const handleCompanyChange = (id) => {
    setSelectedCompanyId(id);
    if (id === "__none__") {
      setForm(f => ({ ...f, exporter_name_address: "", iec: "" }));
      return;
    }
    const co = companies.find(c => c.id === id);
    if (!co) return;
    setForm(f => ({
      ...f,
      exporter_name_address: buildAddress(co),
      iec: co.iec || "",
    }));
    toast.success(`Exporter details filled from ${co.name}`);
  };

  const handleShipmentChange = (id) => {
    setSelectedShipmentId(id);
    if (id === "__none__") return;
    const sh = shipments.find(s => s.id === id);
    if (!sh) return;
    if (sh.company_id && sh.company_id !== selectedCompanyId) {
      handleCompanyChange(sh.company_id);
    }
    setForm(f => ({
      ...f,
      invoice_no: sh.invoice_no || f.invoice_no,
      invoice_date: fmtInvDate(sh.created_at) || f.invoice_date,
      shipping_bill_no: sh.invoice_no || f.shipping_bill_no,
      shipping_bill_date: fmtInvDate(sh.created_at) || f.shipping_bill_date,
      declaration_date: fmtInvDate(sh.created_at) || f.declaration_date,
    }));
    toast.success(`Shipment ${sh.shipment_no} details filled`);
  };

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const saveDraft = () => {
    setSaving(true);
    try {
      localStorage.setItem(DRAFT_KEY,
        JSON.stringify({ form, selectedCompanyId, selectedShipmentId }));
      toast.success("Draft saved locally");
    } finally { setSaving(false); }
  };

  const clearDraft = () => {
    if (!window.confirm("Reset the form and clear the saved draft?")) return;
    localStorage.removeItem(DRAFT_KEY);
    setForm(emptyForm);
    setSelectedCompanyId("");
    setSelectedShipmentId("");
    toast.success("Form reset");
  };

  const doPrintAndConsume = async () => {
    if (poolStatus.available === 0) {
      toast.error("Form number pool is empty. Please upload more form numbers before printing.");
      setUploadOpen(true);
      return;
    }
    setPrinting(true);
    try {
      // Consume atomically
      const r = await axios.post(`${API}/edf/consume`, {
        shipment_id: selectedShipmentId || null,
        snapshot: { ...form, company_id: selectedCompanyId },
      }, { headers: getAuthHeader() });
      const usedNo = r.data.form_no;
      const newStatus = r.data.pool_status;
      setForm(f => ({ ...f, form_no: usedNo }));
      setPoolStatus(newStatus);
      setNextFormNo("");
      toast.success(`Form No. ${usedNo} used for this print`);

      // Low-stock popup after consumption
      if (newStatus.available > 0 && newStatus.available <= newStatus.low_stock_threshold) {
        setTimeout(() => {
          window.alert(
            `Only ${newStatus.available} form number${newStatus.available === 1 ? '' : 's'} remain — please upload more form numbers.`
          );
        }, 300);
      }
      // Give React a tick to paint the number into the form before printing
      setTimeout(() => {
        window.print();
        // After print, refresh peek so the next print picks the next number
        refreshPool();
        // Clear the form_no field so a stale one doesn't get re-printed if user clicks again
        setTimeout(() => setForm(f => ({ ...f, form_no: "" })), 500);
      }, 400);
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error("Form number pool is EMPTY — please upload more numbers.");
        setUploadOpen(true);
      } else {
        toast.error(e.response?.data?.detail || "Print failed");
      }
    } finally {
      setPrinting(false);
    }
  };

  const submitUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      const r = await axios.post(`${API}/edf/upload-pool`, fd,
                                 { headers: { ...getAuthHeader() } });
      toast.success(`Added ${r.data.inserted} form number(s) · ${r.data.skipped_or_duplicate} duplicates skipped`);
      setPoolStatus(r.data.pool_status);
      setUploadOpen(false);
      setUploadFile(null);
      await refreshPool();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const r = await axios.get(`${API}/edf/history?limit=500`, { headers: getAuthHeader() });
      setHistoryRows(r.data.history || []);
    } catch (e) {
      toast.error("Failed to load history");
    } finally { setHistoryLoading(false); }
  }, []);

  const openHistory = () => {
    setHistoryOpen(true);
    loadHistory();
  };

  const exportHistoryCSV = () => {
    const cols = [
      "Form No", "Printed At", "Printed By", "Invoice No", "Shipping Bill No",
      "Exporter", "Consignee", "Country", "Invoice Amount", "Net Realisable",
    ];
    const rows = historyRows.map(h => {
      const s = h.snapshot || {};
      return [
        h.form_no,
        h.printed_at,
        h.printed_by,
        s.invoice_no || "",
        s.shipping_bill_no || "",
        (s.exporter_name_address || "").replace(/\n/g, " "),
        (s.consignee_name_address || "").replace(/\n/g, " "),
        s.country_of_destination || "",
        s.invoice_amount || "",
        s.net_realisable_value || "",
      ];
    });
    const csv = [cols, ...rows].map(r =>
      r.map(v => {
        const s = String(v ?? "");
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(",")
    ).join("\n");
    const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `edf_history_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} entr${rows.length === 1 ? "y" : "ies"}`);
  };

  const reprintFromHistory = (row) => {
    // Load the snapshot into the current form so the user can reprint it
    // (the form_no field stays as the historical number, non-editable).
    const snap = row.snapshot || {};
    setForm(f => ({ ...f, ...snap, form_no: row.form_no }));
    if (snap.company_id) setSelectedCompanyId(snap.company_id);
    setHistoryOpen(false);
    toast.success(`Loaded ${row.form_no} — scroll down and use browser Print (Ctrl+P)`);
    setTimeout(() => window.scrollTo({ top: 200, behavior: "smooth" }), 200);
  };

  const filteredHistory = historyRows.filter(h => {
    const q = historySearch.trim().toLowerCase();
    if (!q) return true;
    const s = h.snapshot || {};
    return (
      (h.form_no || "").toLowerCase().includes(q) ||
      (h.printed_by || "").toLowerCase().includes(q) ||
      (s.invoice_no || "").toLowerCase().includes(q) ||
      (s.exporter_name_address || "").toLowerCase().includes(q) ||
      (s.consignee_name_address || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="p-4 md:p-6 space-y-4 min-h-full bg-slate-50" data-testid="edf-form-panel">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3 print:hidden">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-7 h-7 text-cyan-700" />
            EDF Form — Export Declaration Form (RBI)
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Select a Sender Company to auto-fill. Each print consumes the next form number from the pool.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={`h-9 px-3 text-sm ${
            poolStatus.available === 0 ? "bg-red-100 text-red-800 border border-red-300" :
            poolStatus.low_stock ? "bg-amber-100 text-amber-800 border border-amber-300" :
            "bg-emerald-100 text-emerald-800 border border-emerald-300"}`}
            data-testid="edf-pool-badge">
            <Hash className="w-3.5 h-3.5 mr-1" />
            {poolStatus.available} form no{poolStatus.available === 1 ? "" : "s"} left
          </Badge>
          <Button size="sm" variant="outline" onClick={openHistory}
                  data-testid="edf-history-open">
            <History className="w-4 h-4 mr-1" /> History
          </Button>
          <Button size="sm" variant="outline" onClick={() => setUploadOpen(true)}
                  data-testid="edf-upload-open">
            <Upload className="w-4 h-4 mr-1" /> Upload Form Numbers
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap print:hidden">
        <div className="w-64">
          <Select value={selectedCompanyId || "__none__"} onValueChange={handleCompanyChange}>
            <SelectTrigger data-testid="edf-company-select">
              <SelectValue placeholder="Select Sender Company" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— None —</SelectItem>
              {companies.map(c => (
                <SelectItem key={c.id} value={c.id} data-testid={`edf-company-option-${c.id}`}>
                  <Building2 className="w-3 h-3 inline mr-1" /> {c.name} · {c.gstin}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-64">
          <Select value={selectedShipmentId || "__none__"} onValueChange={handleShipmentChange}>
            <SelectTrigger data-testid="edf-shipment-select">
              <SelectValue placeholder="Autofill from Shipment (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— None —</SelectItem>
              {shipments.map(s => (
                <SelectItem key={s.id} value={s.id} data-testid={`edf-shipment-option-${s.id}`}>
                  <ClipboardCheck className="w-3 h-3 inline mr-1" />
                  {s.shipment_no}{s.invoice_no ? ` · ${s.invoice_no}` : ""}{s.customer_name ? ` · ${s.customer_name}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 text-xs text-slate-500">
          Next form no on print: <span className="font-mono font-semibold text-slate-800">{nextFormNo || "—"}</span>
        </div>
        <Button size="sm" variant="outline" onClick={saveDraft} disabled={saving}
                data-testid="edf-save-draft">
          <Save className="w-4 h-4 mr-1" /> Save Draft
        </Button>
        <Button size="sm" variant="outline" onClick={clearDraft}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="edf-reset">
          <RotateCcw className="w-4 h-4 mr-1" /> Reset
        </Button>
        <Button size="sm" onClick={doPrintAndConsume}
                disabled={printing || poolStatus.available === 0}
                className="bg-cyan-700 hover:bg-cyan-800 text-white"
                data-testid="edf-print">
          {printing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Printer className="w-4 h-4 mr-1" />}
          Print & Consume Number
        </Button>
      </div>

      {poolStatus.available > 0 && poolStatus.low_stock && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800 print:hidden"
             data-testid="edf-low-stock-banner">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div>
            <strong>Low stock:</strong> only {poolStatus.available} form number{poolStatus.available === 1 ? '' : 's'} remain.
            Please upload more form numbers using the button above.
          </div>
        </div>
      )}

      {/* Printable form */}
      <div ref={printRef} className="edf-form-print bg-white shadow border border-slate-200 mx-auto p-6 max-w-[900px]" data-testid="edf-form-body">
        <div className="text-right text-[10px] italic mb-1">Annex I</div>
        <div className="text-center border-b-2 border-slate-800 pb-1 mb-3">
          <div className="text-lg font-bold tracking-wide">EXPORT DECLARATION FORM</div>
        </div>

        <div className="text-[11px] font-bold uppercase text-slate-700 mb-1">General Information:</div>

        <table className="w-full text-xs border border-slate-800 border-collapse mb-2">
          <tbody>
            <tr>
              <td className="border border-slate-700 p-1 w-1/4 font-medium">Customs Security No.:</td>
              <td className="border border-slate-700 p-1 w-1/4">
                <EdfField value={form.customs_security_no} onChange={(v) => upd("customs_security_no", v)} data-testid="edf-cust-sec-no" />
              </td>
              <td className="border border-slate-700 p-1 w-1/4 font-medium">Form No:</td>
              <td className="border border-slate-700 p-1 w-1/4">
                <EdfField value={form.form_no || `next: ${nextFormNo}`} readOnly onChange={() => {}}
                          data-testid="edf-form-no"
                          className={form.form_no ? "font-mono font-semibold bg-cyan-50/50" : "italic text-slate-400"} />
              </td>
            </tr>
            <tr>
              <td className="border border-slate-700 p-1 font-medium">Nature of Cargo:</td>
              <td className="border border-slate-700 p-1" colSpan={3}>
                <EdfField value={form.nature_of_cargo} onChange={(v) => upd("nature_of_cargo", v)} placeholder="Government / Non-Government" />
              </td>
            </tr>
            <tr>
              <td className="border border-slate-700 p-1 font-medium">Mode of Transport:</td>
              <td className="border border-slate-700 p-1" colSpan={3}>
                <EdfField value={form.mode_of_transport} onChange={(v) => upd("mode_of_transport", v)} placeholder="Air / Land / Sea / Post/Couriers / Others" />
              </td>
            </tr>
            <tr>
              <td className="border border-slate-700 p-1 font-medium">Shipping Bill No.</td>
              <td className="border border-slate-700 p-1">
                <EdfField value={form.shipping_bill_no} onChange={(v) => upd("shipping_bill_no", v)} data-testid="edf-sb-no" />
              </td>
              <td className="border border-slate-700 p-1 font-medium">DATE:</td>
              <td className="border border-slate-700 p-1">
                <EdfField value={form.shipping_bill_date} onChange={(v) => upd("shipping_bill_date", v)} placeholder="dd-mm-yyyy" data-testid="edf-sb-date" />
              </td>
            </tr>
            <tr>
              <td className="border border-slate-700 p-1 font-medium">Category of Exporter:</td>
              <td className="border border-slate-700 p-1" colSpan={3}>
                <EdfField value={form.category_of_exporter} onChange={(v) => upd("category_of_exporter", v)} placeholder="Custom (DTA) / SEZ / Status holder / 100% EOU / Warehouse export" />
              </td>
            </tr>
            <tr>
              <td className="border border-slate-700 p-1 font-medium">RBI approval no. &amp; date, if any:</td>
              <td className="border border-slate-700 p-1" colSpan={3}>
                <EdfField value={form.rbi_approval} onChange={(v) => upd("rbi_approval", v)} />
              </td>
            </tr>
            <tr>
              <td className="border border-slate-700 p-1 font-medium">IE CODE:</td>
              <td className="border border-slate-700 p-1">
                <EdfField value={form.iec} onChange={(v) => upd("iec", v)} data-testid="edf-iec"
                          className={selectedCompanyId && form.iec ? "bg-cyan-50/50 font-semibold" : ""} />
              </td>
              <td className="border border-slate-700 p-1 font-medium">AD CODE:</td>
              <td className="border border-slate-700 p-1">
                <EdfField value={form.ad_code} onChange={(v) => upd("ad_code", v)} data-testid="edf-ad-code" />
              </td>
            </tr>
            <tr>
              <td className="border border-slate-700 p-1 font-medium align-top">Exporter&apos;s Name &amp; Address:</td>
              <td className="border border-slate-700 p-1" colSpan={3}>
                <EdfField value={form.exporter_name_address} onChange={(v) => upd("exporter_name_address", v)}
                          multiline data-testid="edf-exporter"
                          className={selectedCompanyId && form.exporter_name_address ? "bg-cyan-50/50 font-semibold" : ""} />
              </td>
            </tr>
            <tr>
              <td className="border border-slate-700 p-1 font-medium align-top">AD Name &amp; Address:</td>
              <td className="border border-slate-700 p-1" colSpan={3}>
                <EdfField value={form.ad_name_address} onChange={(v) => upd("ad_name_address", v)} multiline
                          placeholder="e.g. AXIS BANK, LAKADGANJ, NAGPUR, INDIA" data-testid="edf-ad-name" />
              </td>
            </tr>
            <tr>
              <td className="border border-slate-700 p-1 font-medium align-top">Consignee&apos;s Name &amp; Address:</td>
              <td className="border border-slate-700 p-1" colSpan={3}>
                <EdfField value={form.consignee_name_address} onChange={(v) => upd("consignee_name_address", v)}
                          multiline data-testid="edf-consignee" />
              </td>
            </tr>
            <tr>
              <td className="border border-slate-700 p-1 font-medium">Port of Loading:</td>
              <td className="border border-slate-700 p-1">
                <EdfField value={form.port_of_loading} onChange={(v) => upd("port_of_loading", v)} />
              </td>
              <td className="border border-slate-700 p-1 font-medium">Port of Discharge:</td>
              <td className="border border-slate-700 p-1">
                <EdfField value={form.port_of_discharge} onChange={(v) => upd("port_of_discharge", v)} />
              </td>
            </tr>
            <tr>
              <td className="border border-slate-700 p-1 font-medium align-top">Third Party Name &amp; Address:</td>
              <td className="border border-slate-700 p-1" colSpan={3}>
                <EdfField value={form.third_party_name_address} onChange={(v) => upd("third_party_name_address", v)}
                          multiline placeholder="If any (third-party payments)" />
              </td>
            </tr>
            <tr>
              <td className="border border-slate-700 p-1 font-medium">Country of Destination:</td>
              <td className="border border-slate-700 p-1" colSpan={3}>
                <EdfField value={form.country_of_destination} onChange={(v) => upd("country_of_destination", v)} />
              </td>
            </tr>
            <tr>
              <td className="border border-slate-700 p-1 font-medium">General Commodity Description:</td>
              <td className="border border-slate-700 p-1" colSpan={3}>
                <EdfField value={form.commodity_description} onChange={(v) => upd("commodity_description", v)} />
              </td>
            </tr>
            <tr>
              <td className="border border-slate-700 p-1 font-medium">Payment through ACU?</td>
              <td className="border border-slate-700 p-1">
                <EdfField value={form.payment_acu} onChange={(v) => upd("payment_acu", v)} placeholder="Yes/No" />
              </td>
              <td className="border border-slate-700 p-1 font-medium">LEO Date:</td>
              <td className="border border-slate-700 p-1">
                <EdfField value={form.leo_date} onChange={(v) => upd("leo_date", v)} placeholder="dd-mm-yyyy" />
              </td>
            </tr>
            <tr>
              <td className="border border-slate-700 p-1 font-medium">State of Origin:</td>
              <td className="border border-slate-700 p-1">
                <EdfField value={form.state_of_origin} onChange={(v) => upd("state_of_origin", v)} />
              </td>
              <td className="border border-slate-700 p-1 font-medium">Custom Assessable Value (INR)</td>
              <td className="border border-slate-700 p-1">
                <EdfField value={form.custom_assessable_value} onChange={(v) => upd("custom_assessable_value", v)} />
              </td>
            </tr>
            <tr>
              <td className="border border-slate-700 p-1 font-medium">Total FOB value in words (INR):</td>
              <td className="border border-slate-700 p-1" colSpan={3}>
                <EdfField value={form.fob_words} onChange={(v) => upd("fob_words", v)} />
              </td>
            </tr>
          </tbody>
        </table>

        {/* Section 2 */}
        <div className="text-[11px] font-bold uppercase text-slate-700 mb-1 mt-2">2. Invoice Wise details of Export Value</div>
        <table className="w-full text-xs border border-slate-800 border-collapse mb-2">
          <tbody>
            <tr>
              <td className="border border-slate-700 p-1 font-medium">Invoice No.</td>
              <td className="border border-slate-700 p-1">
                <EdfField value={form.invoice_no} onChange={(v) => upd("invoice_no", v)} data-testid="edf-inv-no" />
              </td>
              <td className="border border-slate-700 p-1 font-medium">Invoice Date</td>
              <td className="border border-slate-700 p-1">
                <EdfField value={form.invoice_date} onChange={(v) => upd("invoice_date", v)} data-testid="edf-inv-date" />
              </td>
            </tr>
            <tr>
              <td className="border border-slate-700 p-1 font-medium">Invoice Currency</td>
              <td className="border border-slate-700 p-1">
                <EdfField value={form.invoice_currency} onChange={(v) => upd("invoice_currency", v)} />
              </td>
              <td className="border border-slate-700 p-1 font-medium">Invoice Amount</td>
              <td className="border border-slate-700 p-1">
                <EdfField value={form.invoice_amount} onChange={(v) => upd("invoice_amount", v)} />
              </td>
            </tr>
            <tr>
              <td className="border border-slate-700 p-1 font-medium">Nature of Contract</td>
              <td className="border border-slate-700 p-1" colSpan={3}>
                <EdfField value={form.nature_of_contract} onChange={(v) => upd("nature_of_contract", v)}
                          placeholder="FOB / CIF / C&F / CI / Others" />
              </td>
            </tr>
          </tbody>
        </table>

        <table className="w-full text-[10px] border border-slate-800 border-collapse mb-2">
          <thead className="bg-slate-100">
            <tr>
              <th className="border border-slate-700 p-1 text-left">Particulars</th>
              <th className="border border-slate-700 p-1">Currency</th>
              <th className="border border-slate-700 p-1">Amount in FC</th>
              <th className="border border-slate-700 p-1">Exchange Rate</th>
              <th className="border border-slate-700 p-1">Amount (INR)</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["FOB Value", "fob_fc", "fob_rate", "fob_inr"],
              ["Freight", "freight_fc", "freight_rate", "freight_inr"],
              ["Insurance", "insurance_fc", "insurance_rate", "insurance_inr"],
              ["Commission", "commission_fc", null, null],
              ["Discount", "discount_fc", null, null],
              ["Other Deduction", "other_deduction_fc", null, null],
              ["Packing Charges", "packing_fc", "packing_rate", "packing_inr"],
            ].map(([label, fcKey, rateKey, inrKey]) => (
              <tr key={label}>
                <td className="border border-slate-700 p-1 font-medium">{label}</td>
                <td className="border border-slate-700 p-1 w-14">
                  <EdfField value={form.invoice_currency} readOnly onChange={() => {}} />
                </td>
                <td className="border border-slate-700 p-1">
                  <EdfField value={form[fcKey]} onChange={(v) => upd(fcKey, v)} />
                </td>
                <td className="border border-slate-700 p-1">
                  {rateKey ? <EdfField value={form[rateKey]} onChange={(v) => upd(rateKey, v)} /> : null}
                </td>
                <td className="border border-slate-700 p-1">
                  {inrKey ? <EdfField value={form[inrKey]} onChange={(v) => upd(inrKey, v)} /> : null}
                </td>
              </tr>
            ))}
            <tr>
              <td className="border border-slate-700 p-1 font-bold" colSpan={4}>Net Realisable value</td>
              <td className="border border-slate-700 p-1">
                <EdfField value={form.net_realisable_value} onChange={(v) => upd("net_realisable_value", v)}
                          className="font-semibold" />
              </td>
            </tr>
          </tbody>
        </table>

        {/* Section 3 */}
        <div className="text-[11px] font-bold uppercase text-slate-700 mb-1 mt-2">3. Applicable for Export under FPO/Couriers</div>
        <table className="w-full text-xs border border-slate-800 border-collapse mb-2">
          <tbody>
            <tr>
              <td className="border border-slate-700 p-1 font-medium">Name of the post Office:</td>
              <td className="border border-slate-700 p-1">
                <EdfField value={form.post_office_name} onChange={(v) => upd("post_office_name", v)} />
              </td>
              <td className="border border-slate-700 p-1 font-medium">Parcel receipts No. &amp; Date:</td>
              <td className="border border-slate-700 p-1">
                <EdfField value={form.parcel_receipts} onChange={(v) => upd("parcel_receipts", v)} />
              </td>
            </tr>
          </tbody>
        </table>

        {/* Section 4 — declaration */}
        <div className="text-[11px] font-bold uppercase text-slate-700 mb-1 mt-2">4. Declaration by the Exporter</div>
        <p className="text-[10px] leading-tight text-slate-700 mb-2">
          I/We hereby declare that I/we am/are the seller/consignor of the goods in respect of which this declaration is made
          and that the particulars given above are true and that the value to be received from the buyer represents the export
          value contracted and declared above. I/We undertake that I/we will deliver to the authorised dealer bank named above
          the foreign exchange representing the full value of the goods exported as above on or before the period stipulated by
          RBI from time to time. I/We am/are not in the Caution List of the Reserve Bank of India.
        </p>
        <div className="grid grid-cols-2 gap-4 text-xs mb-4">
          <div>
            <Label className="text-[10px] text-slate-500">Date:</Label>
            <EdfField value={form.declaration_date} onChange={(v) => upd("declaration_date", v)} placeholder="dd-mm-yyyy" />
          </div>
          <div className="text-center border-t border-slate-700 pt-1 self-end">
            (Signature of Exporter)
          </div>
        </div>

        {/* Section 5 */}
        <div className="text-[11px] font-bold uppercase text-slate-700 mb-1 mt-2">
          5. Space for use of the competent authority (Custom/SEZ):
        </div>
        <p className="text-[10px] leading-tight text-slate-700 mb-6">
          Certified, on the basis of above declaration by the Custom/SEZ unit, that the goods described above and the export
          value declared by the exporter in this form is as per the corresponding invoice / gist of invoices submitted and
          declared by the unit.
        </p>
        <div className="grid grid-cols-2 gap-4 text-xs">
          <div>
            <Label className="text-[10px] text-slate-500">Date:</Label>
            <div className="border-b border-slate-300 h-5"></div>
          </div>
          <div className="text-center border-t border-slate-700 pt-1 self-end">
            (Signature of Designated/Authorised officials of Custom/SEZ)
          </div>
        </div>
      </div>

      {/* History dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-5xl" data-testid="edf-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-cyan-700" />
              EDF Print History
              <Badge className="bg-slate-100 text-slate-700 text-[11px]">{historyRows.length} entries</Badge>
            </DialogTitle>
            <DialogDescription>
              Every consumed form number is logged here for RBI audit trail cross-checking.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 pb-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search by form no / invoice / exporter / consignee / user…"
                     value={historySearch} onChange={(e) => setHistorySearch(e.target.value)}
                     className="pl-8 h-9 text-sm" data-testid="edf-history-search" />
            </div>
            <Button size="sm" variant="outline" onClick={loadHistory} disabled={historyLoading}
                    data-testid="edf-history-refresh">
              {historyLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              : <History className="w-4 h-4 mr-1" />} Refresh
            </Button>
            <Button size="sm" onClick={exportHistoryCSV}
                    disabled={filteredHistory.length === 0}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="edf-history-export">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> Export CSV
            </Button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto border rounded">
            {filteredHistory.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-500" data-testid="edf-history-empty">
                {historyLoading ? "Loading…" : "No history entries match the current filter."}
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5">Form No</th>
                    <th className="text-left px-2 py-1.5">Printed At</th>
                    <th className="text-left px-2 py-1.5">Printed By</th>
                    <th className="text-left px-2 py-1.5">Invoice</th>
                    <th className="text-left px-2 py-1.5">Consignee</th>
                    <th className="text-left px-2 py-1.5">Amount</th>
                    <th className="text-right px-2 py-1.5">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHistory.map(h => {
                    const s = h.snapshot || {};
                    return (
                      <tr key={h.id} className="border-b hover:bg-slate-50"
                          data-testid={`edf-history-row-${h.form_no}`}>
                        <td className="px-2 py-1.5 font-mono font-semibold text-cyan-800">{h.form_no}</td>
                        <td className="px-2 py-1.5 text-slate-600">
                          {h.printed_at ? new Date(h.printed_at).toLocaleString() : "—"}
                        </td>
                        <td className="px-2 py-1.5">{h.printed_by}</td>
                        <td className="px-2 py-1.5">
                          {s.invoice_no || "—"}
                          {s.invoice_date && <div className="text-[10px] text-slate-400">{s.invoice_date}</div>}
                        </td>
                        <td className="px-2 py-1.5 max-w-xs truncate" title={s.consignee_name_address}>
                          {s.consignee_name_address || "—"}
                        </td>
                        <td className="px-2 py-1.5">
                          {s.invoice_amount ? `${s.invoice_currency || ""} ${s.invoice_amount}` : "—"}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                                  onClick={() => reprintFromHistory(h)}
                                  data-testid={`edf-history-reprint-${h.form_no}`}>
                            <Printer className="w-3 h-3 mr-1" /> Reprint
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload numbers dialog */}
      <Dialog open={uploadOpen} onOpenChange={(o) => { if (!o) { setUploadOpen(false); setUploadFile(null); } }}>
        <DialogContent className="max-w-md" data-testid="edf-upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListOrdered className="w-5 h-5 text-cyan-700" /> Upload EDF Form Numbers
            </DialogTitle>
            <DialogDescription>
              Upload an Excel (.xlsx) or CSV containing one form number per row.
              Duplicates are silently skipped. Current pool: <strong>{poolStatus.available}</strong> available / <strong>{poolStatus.total}</strong> total.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-xs">File</Label>
            <Input type="file" accept=".xlsx,.xls,.csv,.txt"
                   onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                   data-testid="edf-upload-file" />
            {uploadFile && (
              <p className="text-[11px] text-slate-500">{uploadFile.name} · {(uploadFile.size / 1024).toFixed(1)} KB</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} disabled={uploading}>Cancel</Button>
            <Button onClick={submitUpload} disabled={!uploadFile || uploading}
                    className="bg-cyan-700 hover:bg-cyan-800 text-white"
                    data-testid="edf-upload-submit">
              {uploading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Uploading…</>
                        : <><Upload className="w-4 h-4 mr-1" /> Upload</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .edf-form-print {
            border: none !important; box-shadow: none !important;
            margin: 0 !important; max-width: none !important; padding: 10mm !important;
          }
          .edf-form-print input, .edf-form-print textarea {
            border: none !important; background: transparent !important; color: black !important;
          }
          @page { size: A4; margin: 6mm; }
        }
      `}</style>
    </div>
  );
};

export default EdfFormPanel;
